import Layout from "@/components/Layout";
import { formatCurrency } from "@/lib/currencies/currenciesStore";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { Button } from "@/components/ui/button";
import { deleteEmployee, employeeApiErrorMessage, getEmployeeById } from "@/lib/hr/employeesStore";
import { listLeaveRequestsForEmployee, syncLeaveRequestsFromApi } from "@/lib/hr/leaveRequestsStore";
import type { EmployeeAllowance, EmployeeDeduction, EmployeeRecord } from "@/lib/hr/employeesStore";
import { listLeavePolicies, syncLeavePoliciesFromApi } from "@/lib/hr/leavePoliciesStore";
import { listLeaveYears, syncLeaveYearsFromApi } from "@/lib/hr/leaveYearsStore";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatTimeDisplayAmPm } from "@/components/hr/TimePickerField";
import {
  ArrowLeft,
  Banknote,
  Briefcase,
  Building2,
  Calendar,
  Clock,
  ClipboardList,
  CreditCard,
  Edit,
  FileCheck2,
  Home,
  Mail,
  MapPin,
  Phone,
  Trash2,
  User,
  UserCircle2,
  Users,
  Wallet,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useMinWidth } from "@/hooks/use-min-width";
import { can, getStoredAuthUser } from "@/lib/authUser";

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function parseMoney(raw: string): number {
  const t = String(raw ?? "").replace(/[^\d.]/g, "");
  const n = Number(t);
  return Number.isFinite(n) ? n : 0;
}

function formatLongDate(d: string): string {
  const t = (d || "").trim().slice(0, 10);
  if (!t) return "—";
  // Expecting YYYY-MM-DD from API.
  const dt = new Date(`${t}T00:00:00`);
  if (!Number.isFinite(dt.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric", year: "numeric" }).format(dt);
}

/** Salary breakdown: show Rs. 0 for zero amounts (basic / totals may be zero). */
function formatPkMoney(n: number): string {
  if (!Number.isFinite(n)) return "—";
  return formatCurrency(n);
}

function formatStreetAddress(e: EmployeeRecord, kind: "present" | "permanent"): string {
  if (kind === "present") {
    const lines = [e.presentAddressLine1, e.presentAddressLine2].filter((x) => x.trim());
    const region = [e.presentCity, e.presentState, e.presentCountry].filter((x) => x.trim()).join(", ");
    const block = lines.length ? lines.join(", ") : "";
    if (block && region) return `${block}, ${region}`;
    return block || region || "—";
  }
  const lines = [e.permanentAddressLine1, e.permanentAddressLine2].filter((x) => x.trim());
  const region = [e.permanentCity, e.permanentState, e.permanentCountry].filter((x) => x.trim()).join(", ");
  const block = lines.length ? lines.join(", ") : "";
  if (block && region) return `${block}, ${region}`;
  return block || region || "—";
}

function InfoRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  const display = value.trim() || "—";
  return (
    <div className="flex gap-3 py-3 border-b border-border/40 last:border-0">
      <div className="shrink-0 mt-0.5 text-muted-foreground">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="text-sm text-foreground mt-1 break-words">{display}</p>
      </div>
    </div>
  );
}

function DetailSection({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <div className="glass-card p-6">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-primary shrink-0" />
        <h3 className="font-display font-semibold text-foreground text-sm">{title}</h3>
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function SalaryLineItem({ prefix, label, amount }: { prefix?: string; label: string; amount: number }) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm">
      <span className="text-foreground min-w-0">
        {prefix ? <span className="text-muted-foreground">{prefix}</span> : null}
        {label}
      </span>
      <span className="font-medium text-foreground tabular-nums shrink-0">{formatPkMoney(amount)}</span>
    </div>
  );
}

function SalarySubtotalRow({ label, amount, emphasize }: { label: string; amount: number; emphasize?: boolean }) {
  return (
    <div
      className={cn(
        "flex items-baseline justify-between gap-4 pt-2 mt-2 border-t border-border/40",
        emphasize && "font-semibold",
      )}
    >
      <span className={cn("text-sm", emphasize ? "text-foreground" : "text-foreground")}>{label}</span>
      <span className={cn("tabular-nums shrink-0", emphasize ? "font-display text-lg font-bold text-primary" : "text-sm font-semibold text-foreground")}>
        {formatPkMoney(amount)}
      </span>
    </div>
  );
}

export default function EmployeeView() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [leaveVersion, setLeaveVersion] = useState(0);
  const [refVersion, setRefVersion] = useState(0);

  const user = useMemo(() => getStoredAuthUser(), []);
  const canView = can(user, "employee_can_view");
  const canEdit = can(user, "employee_can_update");
  const canDelete = can(user, "employee_can_delete");

  const employee = useMemo(() => (id ? getEmployeeById(id) : undefined), [id]);

  useEffect(() => {
    if (!canView) {
      toast.error("User does not have the right permissions.");
      navigate("/dashboard", { replace: true });
    }
  }, [canView, navigate]);

  useEffect(() => {
    if (!id) return;
    void syncLeaveRequestsFromApi({ employee_id: id })
      .then(() => setLeaveVersion((v) => v + 1))
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    void Promise.all([syncLeaveYearsFromApi(), syncLeavePoliciesFromApi()])
      .then(() => setRefVersion((v) => v + 1))
      .catch(() => {});
  }, []);

  const leaves = useMemo(() => (id ? listLeaveRequestsForEmployee(id) : []), [id, leaveVersion]);
  const leaveYears = useMemo(() => listLeaveYears(), [refVersion]);
  const leavePolicies = useMemo(() => listLeavePolicies(), [refVersion]);

  const leaveYearTabs = useMemo(() => {
    const yearsFromAssignments =
      employee?.leavePolicyAssignments?.map((a) => ({
        id: a.leaveYearId,
        label: a.leaveYearLabel,
        startDate: a.leaveYearStartDate,
        endDate: a.leaveYearEndDate,
        active: a.leaveYearActive,
      })) ?? [];

    const yearsFromAllocations =
      employee?.leaveAllocations?.map((a) => ({
        id: a.leaveYearId,
        label: a.leaveYearLabel,
        startDate: a.leaveYearStartDate,
        endDate: a.leaveYearEndDate,
        active: a.leaveYearActive,
      })) ?? [];

    const byId = new Map<string, { id: string; label: string; startDate: string; endDate: string; active: boolean }>();
    for (const y of [...yearsFromAssignments, ...yearsFromAllocations]) {
      if (!y.id) continue;
      if (!byId.has(y.id)) byId.set(y.id, y);
      else if (y.active) byId.set(y.id, y);
    }

    const merged = Array.from(byId.values());
    merged.sort((a, b) => {
      if (a.active !== b.active) return a.active ? -1 : 1;
      const ad = a.startDate || "";
      const bd = b.startDate || "";
      return bd.localeCompare(ad) || b.id.localeCompare(a.id);
    });

    // Fallback: if API hasn't provided history yet, fall back to active-only fields + store leaveYears list.
    if (merged.length === 0 && employee?.leaveYearId?.trim()) {
      const yearId = employee.leaveYearId.trim();
      const y = leaveYears.find((x) => String(x.id) === yearId);
      merged.push({
        id: yearId,
        label: y?.label ?? yearId,
        startDate: y?.startDate ?? "",
        endDate: y?.endDate ?? "",
        active: Boolean(y?.active),
      });
    }

    return merged;
  }, [employee?.leavePolicyAssignments, employee?.leaveAllocations, employee?.leaveYearId, leaveYears]);

  const defaultLeaveTab = useMemo(() => {
    const active = leaveYearTabs.find((y) => y.active);
    return active?.id || leaveYearTabs[0]?.id || "leave-year";
  }, [leaveYearTabs]);

  const allowancesTotal = useMemo(() => {
    if (!employee?.allowances?.length) return 0;
    return employee.allowances.reduce((sum, a) => sum + parseMoney(a.amount), 0);
  }, [employee]);

  const deductionsTotal = useMemo(() => {
    if (!employee?.deductions?.length) return 0;
    return employee.deductions.reduce((sum, d) => sum + parseMoney(d.amount), 0);
  }, [employee]);

  const workShiftCurrent = useMemo(() => {
    const list = employee?.workShiftAssignments ?? [];
    if (list.length === 0) return null;
    const currentByOpenEnd = list.find((a) => !a.effectiveTo) ?? null;
    if (currentByOpenEnd) return currentByOpenEnd;
    const id = (employee?.workShiftId ?? "").trim();
    if (!id) return list[0] ?? null;
    return list.find((a) => String(a.workShiftId) === id) ?? (list[0] ?? null);
  }, [employee?.workShiftAssignments, employee?.workShiftId]);

  if (!id) {
    return (
      <Layout title="Employee">
        <div className="glass-card p-8 text-sm text-muted-foreground">Missing employee id.</div>
      </Layout>
    );
  }

  if (!employee) {
    return (
      <Layout title="Employee">
        <div className="space-y-4">
          <div className="glass-card p-8 text-sm text-muted-foreground">Employee not found.</div>
          <Link to="/employees">
            <Button variant="glass" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to employees
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const name = (employee.fullName || `${employee.firstName} ${employee.lastName}`).trim() || "Employee";
  const subtitle = [employee.designationName, employee.departmentName].filter(Boolean).join(" — ") || "—";
  const basicSalary = parseMoney(employee.basicSalary);
  const grossSalary = basicSalary + allowancesTotal;
  const netSalary = grossSalary - deductionsTotal;
  const isActive = employee.employeeStatus.toLowerCase() === "active";
  const isLgUp = useMinWidth(1024);

  return (
    <Layout title={name}>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <Link to="/employees">
              <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground shrink-0 mt-0.5">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </Link>
            <div className="min-w-0">
              <h1 className="text-2xl font-display font-bold text-foreground truncate">{name}</h1>
              <p className="text-sm text-muted-foreground mt-1 truncate">{subtitle}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {canEdit ? (
              <Link to={`/employees/${employee.id}/edit`}>
                <Button variant="glass" className="gap-2">
                  <Edit className="h-4 w-4" />
                  Edit
                </Button>
              </Link>
            ) : null}
            {canDelete ? (
              <Button
                variant="ghost"
                className="gap-2 text-destructive hover:bg-destructive/10"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            ) : null}
          </div>
        </div>

        <div className="glass-card p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div
              className={cn(
                "relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-border/50 bg-muted/30 text-lg font-bold tracking-tight text-primary",
                employee.photoDataUrl && "border-primary/25",
              )}
              aria-hidden
            >
              {employee.photoDataUrl ? (
                <img src={employee.photoDataUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                initialsFromName(name)
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={cn(
                  "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border",
                  isActive
                    ? "bg-emerald-500/15 text-emerald-800 dark:text-emerald-400 border-emerald-500/25"
                    : "bg-muted/50 text-muted-foreground border-border/60",
                )}
              >
                {employee.employeeStatus || "—"}
              </span>
              {employee.employeeType ? (
                <span className="inline-flex items-center rounded-full border border-border/60 bg-muted/30 px-2.5 py-0.5 text-xs font-medium text-foreground">
                  {employee.employeeType}
                </span>
              ) : null}
              {employee.employeeCode ? (
                <span className="inline-flex items-center rounded-full border border-border/60 bg-background/80 px-2.5 py-0.5 font-mono text-xs font-medium text-foreground">
                  {employee.employeeCode}
                </span>
              ) : null}
            </div>
          </div>
        </div>

        {(() => {
          const basicSection = () => (
          <DetailSection title="Basic Information" icon={UserCircle2}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6">
              <InfoRow icon={CreditCard} label="CNIC" value={employee.cnic} />
              <InfoRow icon={Phone} label="Phone" value={employee.primaryContact} />
              <InfoRow
                icon={employee.gender === "female" ? User : Users}
                label={employee.gender === "female" ? "Husband Name" : "Father Name"}
                value={employee.fatherHusbandName.trim() || "—"}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6">
              <InfoRow icon={Mail} label="Email" value={employee.email} />
              <InfoRow icon={MapPin} label="City" value={employee.presentCity} />
              <div className="hidden md:block" />
            </div>

            <InfoRow icon={Home} label="Current Address" value={formatStreetAddress(employee, "present")} />
            <InfoRow icon={Home} label="Permanent Address" value={formatStreetAddress(employee, "permanent")} />
          </DetailSection>
          );

          const employmentSection = () => (
          <DetailSection title="Employment Information" icon={Briefcase}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6">
              <InfoRow icon={Building2} label="Department" value={employee.departmentName} />
              <InfoRow icon={Briefcase} label="Designation" value={employee.designationName} />
              <InfoRow
                icon={User}
                label="Reporting To"
                value={
                  employee.reportingTo.trim()
                    ? (() => {
                        const manager = getEmployeeById(employee.reportingTo.trim());
                        if (!manager) return employee.reportingTo;
                        const name =
                          manager.fullName.trim() ||
                          `${manager.firstName} ${manager.lastName}`.trim() ||
                          `Employee #${manager.id}`;
                        return manager.employeeCode.trim() ? `${name} (${manager.employeeCode})` : name;
                      })()
                    : "—"
                }
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6">
              <InfoRow icon={Calendar} label="Joining Date" value={employee.joiningDate} />
              <InfoRow icon={Calendar} label="Confirmation Date" value={employee.confirmationDate} />
              <div className="hidden md:block" />
            </div>
          </DetailSection>
          );

          const salarySection = () => (
          <DetailSection title="Salary Breakdown" icon={Wallet}>
            <div className="space-y-1">
              <SalaryLineItem label="Basic Salary" amount={basicSalary} />
            </div>

            <div className="mt-5 space-y-2">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Allowances</p>
              {employee.allowances.length === 0 ? (
                <p className="text-sm text-muted-foreground py-0.5">No allowances recorded.</p>
              ) : (
                employee.allowances.map((a: EmployeeAllowance) => (
                  <SalaryLineItem
                    key={a.id}
                    prefix="- "
                    label={(a.name || "").trim() || "Allowance"}
                    amount={parseMoney(a.amount)}
                  />
                ))
              )}
              <SalarySubtotalRow label="Total Allowances" amount={allowancesTotal} />
            </div>

            <SalarySubtotalRow label="Gross Salary" amount={grossSalary} emphasize />

            <div className="mt-5 space-y-2">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Deductions</p>
              {(!employee.deductions || employee.deductions.length === 0) ? (
                <p className="text-sm text-muted-foreground py-0.5">No deductions recorded.</p>
              ) : (
                employee.deductions.map((d: EmployeeDeduction) => (
                  <SalaryLineItem
                    key={d.id}
                    prefix="- "
                    label={(d.name || "").trim() || "Deduction"}
                    amount={parseMoney(d.amount)}
                  />
                ))
              )}
              <SalarySubtotalRow label="Total Deductions" amount={deductionsTotal} />
            </div>

            <SalarySubtotalRow label="Net Salary" amount={netSalary} emphasize />
          </DetailSection>
          );

          const bankSection = () => (
          <DetailSection title="Bank Information" icon={Banknote}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6">
              <InfoRow icon={Building2} label="Bank Name" value={employee.bankName} />
              <InfoRow icon={CreditCard} label="Account Number" value={employee.bankAccountNumber} />
              <InfoRow icon={User} label="Account Holder" value={employee.accountHolderName} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
              <InfoRow icon={Wallet} label="Payment Method" value={employee.paymentMethodType} />
            </div>
          </DetailSection>
          );

          const leaveSection = () => (
          <DetailSection title="Leave" icon={ClipboardList}>
            <Tabs defaultValue={defaultLeaveTab} className="w-full">
                    <TabsList className="flex h-auto w-full min-w-0 items-stretch justify-start gap-0 overflow-x-auto rounded-none border-0 border-b border-border/60 bg-transparent p-0">
                      {leaveYearTabs.map((y) => {
                        const label = y.label?.trim() ? y.label : `Leave year ${y.id}`;
                        const isActiveYear = y.active;
                        return (
                          <TabsTrigger
                            key={y.id}
                            value={y.id}
                            className={cn(
                              "relative shrink-0 rounded-none border-0 border-b-2 border-transparent bg-transparent px-4 py-2.5 text-sm font-medium text-foreground shadow-none ring-offset-0",
                              "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                              "data-[state=active]:bg-transparent data-[state=active]:shadow-none",
                              "data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground",
                              "data-[state=active]:-mb-px data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 dark:data-[state=active]:border-blue-400 dark:data-[state=active]:text-blue-400",
                            )}
                          >
                            <span className="truncate max-w-[14rem]" title={label}>
                              {label}
                            </span>
                            {isActiveYear ? (
                              <span className="ml-1.5 text-[10px] font-normal text-muted-foreground">· current</span>
                            ) : null}
                          </TabsTrigger>
                        );
                      })}
                    </TabsList>

                    {leaveYearTabs.map((y) => {
                      const yearId = y.id;
                      const assignment = employee.leavePolicyAssignments?.find((a) => a.leaveYearId === yearId) ?? null;
                      const policy =
                        assignment?.leavePolicyId
                          ? leavePolicies.find((p) => String(p.id) === String(assignment.leavePolicyId)) ?? null
                          : null;
                      const allocations = employee.leaveAllocations.filter((a) => a.leaveYearId === yearId);

                      const yearLabel =
                        y.startDate && y.endDate
                          ? `${formatLongDate(y.startDate)} to ${formatLongDate(y.endDate)}`
                          : "—";

                      const yearLeaves = leaves.filter((r) => String(r.leaveYearId ?? "") === String(yearId));

                      return (
                        <TabsContent key={yearId} value={yearId} className="mt-4">
                          <div className="space-y-3">
                            {/* Period, policy & requests — one summary bar + optional list */}
                            <div
                              className={cn(
                                "rounded-xl border p-3 sm:p-4 border-border/50 bg-muted/10",
                              )}
                            >
                              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 sm:items-stretch">
                                <div className="flex min-w-0 flex-col gap-3 rounded-lg border border-border/50 bg-background/60 p-3 sm:p-4">
                                  <div className="flex items-start gap-3 min-w-0">
                                    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-background/90 border border-border/50">
                                      <Calendar className="h-4 w-4 text-muted-foreground" aria-hidden />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                                        Leave period
                                      </p>
                                      <p className="text-sm font-medium text-foreground leading-snug break-words">{yearLabel}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-start gap-3 min-w-0 border-t border-border/40 pt-3">
                                    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-background/90 border border-border/50">
                                      <Briefcase className="h-4 w-4 text-muted-foreground" aria-hidden />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                                        Leave policy
                                      </p>
                                      <p
                                        className="text-sm font-medium text-foreground leading-snug break-words"
                                        title={policy?.name ?? assignment?.leavePolicyName ?? "—"}
                                      >
                                        {policy?.name ?? assignment?.leavePolicyName ?? "—"}
                                      </p>
                                    </div>
                                  </div>
                                </div>

                                <div className="flex min-h-0 min-w-0 flex-col justify-center rounded-lg border border-border/50 bg-background/60 p-3 sm:p-4">
                                  <div className="flex items-start gap-3">
                                    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-background/90 border border-border/50">
                                      <ClipboardList className="h-4 w-4 text-muted-foreground" aria-hidden />
                                    </div>
                                    <div className="min-w-0">
                                      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                                        Leave requests
                                      </p>
                                      <p className="text-sm font-medium text-foreground tabular-nums">
                                        {yearLeaves.length}{" "}
                                        <span className="text-muted-foreground font-normal text-xs">
                                          {yearLeaves.length === 1 ? "request" : "requests"}
                                        </span>
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {yearLeaves.length > 0 ? (
                                <ul className="mt-3 pt-3 border-t border-border/40 space-y-1.5 max-h-[220px] overflow-y-auto pr-0.5">
                                  {yearLeaves.slice(0, 12).map((r) => (
                                    <li
                                      key={r.id}
                                      className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border/40 bg-background/40 px-2.5 py-1.5 text-xs"
                                    >
                                      <span className="text-foreground min-w-0">
                                        <span className="font-medium">{r.leaveType?.title ?? "Leave"}</span>
                                        <span className="text-muted-foreground">
                                          {" "}
                                          · {r.startDate} → {r.endDate} ({r.days}d)
                                        </span>
                                      </span>
                                      <span
                                        className={cn(
                                          "text-[9px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-full shrink-0",
                                          r.status === "approved" && "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
                                          r.status === "pending" && "bg-amber-500/15 text-amber-800 dark:text-amber-400",
                                          r.status === "rejected" && "bg-destructive/15 text-destructive",
                                          r.status === "cancelled" && "bg-muted text-muted-foreground",
                                        )}
                                      >
                                        {r.status}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="mt-3 pt-3 border-t border-border/40 text-xs text-muted-foreground">
                                  No leave requests in this period.
                                </p>
                              )}
                            </div>

                            {/* Leave by type — table */}
                            {(() => {
                              type CardRow = {
                                key: string;
                                title: string;
                                slug: string;
                                daysAllowed: number | null;
                                maxReq: number | null;
                                maxMo: number | null;
                                total: number;
                                used: number;
                                remaining: number;
                                carriedForward: number;
                                encashedFromPrev: number;
                                baseAllowed: number | undefined;
                                showCarryNote: boolean;
                                showEncashNote: boolean;
                              };

                              const rows: CardRow[] =
                                policy && policy.leaveTypes.length > 0
                                  ? policy.leaveTypes.map((t) => {
                                      const a = allocations.find((x) => String(x.leaveTypeId) === String(t.leaveTypeId));
                                      const total = a?.totalDays ?? 0;
                                      const used = a?.usedDays ?? 0;
                                      const remaining = a?.remainingDays ?? 0;
                                      const carriedForward = Math.max(0, a?.carriedForwardDays || 0);
                                      const encashedFromPrev = Math.max(0, a?.encashedDays || 0);
                                      const baseAllowed = t.daysAllowed;
                                      return {
                                        key: `${yearId}-lt-${t.leaveTypeId}`,
                                        title: t.title || "—",
                                        slug: t.slug || "",
                                        daysAllowed: typeof t.daysAllowed === "number" ? t.daysAllowed : null,
                                        maxReq: typeof t.maxDaysPerRequest === "number" ? t.maxDaysPerRequest : null,
                                        maxMo: typeof t.maxDaysPerMonth === "number" ? t.maxDaysPerMonth : null,
                                        total,
                                        used,
                                        remaining,
                                        carriedForward,
                                        encashedFromPrev,
                                        baseAllowed,
                                        showCarryNote: Boolean(
                                          policy.carryForward && (carriedForward > 0 || typeof baseAllowed === "number"),
                                        ),
                                        showEncashNote: Boolean(
                                          policy.encashmentAllowed &&
                                            (encashedFromPrev > 0 || typeof baseAllowed === "number"),
                                        ),
                                      };
                                    })
                                  : allocations
                                      .slice()
                                      .sort((a, b) => (a.leaveTypeTitle || "").localeCompare(b.leaveTypeTitle || ""))
                                      .map((a) => {
                                        const total = a.totalDays || 0;
                                        const used = a.usedDays || 0;
                                        const remaining = a.remainingDays || 0;
                                        const carriedForward = Math.max(0, a.carriedForwardDays || 0);
                                        const encashedFromPrev = Math.max(0, a.encashedDays || 0);
                                        return {
                                          key: `${yearId}-lt-${a.leaveTypeId}`,
                                          title: a.leaveTypeTitle?.trim() ? a.leaveTypeTitle : "Leave type",
                                          slug: a.leaveTypeSlug?.trim() || "",
                                          daysAllowed: null,
                                          maxReq: null,
                                          maxMo: null,
                                          total,
                                          used,
                                          remaining,
                                          carriedForward,
                                          encashedFromPrev,
                                          baseAllowed: undefined,
                                          showCarryNote: false,
                                          showEncashNote: false,
                                        };
                                      });

                              if (rows.length === 0) {
                                return (
                                  <div className="rounded-xl border border-border/50 bg-muted/10 p-4 text-sm text-muted-foreground">
                                    No leave types or allocations for this period.
                                  </div>
                                );
                              }

                              return (
                                <div>
                                  <p className="text-xs font-semibold text-foreground mb-1">Leave by type</p>
                                  <p className="text-[11px] text-muted-foreground mb-3">
                                    Policy limits and your balance for each leave type.
                                  </p>
                                  <div className="overflow-x-auto rounded-xl border border-border/50">
                                    <table className="w-full min-w-[46rem] text-sm">
                                      <thead>
                                        <tr className="border-b border-border/50 bg-muted/30 text-[10px] uppercase tracking-wide text-muted-foreground">
                                          <th className="text-left font-semibold py-2.5 pl-3 pr-2">Leave type</th>
                                          <th className="text-right font-semibold py-2.5 px-2 whitespace-nowrap">Allowed</th>
                                          <th className="text-right font-semibold py-2.5 px-2 whitespace-nowrap">Max / req</th>
                                          <th className="text-right font-semibold py-2.5 px-2 whitespace-nowrap">Max / mo</th>
                                          <th className="text-right font-semibold py-2.5 px-2 whitespace-nowrap">Total</th>
                                          <th className="text-right font-semibold py-2.5 px-2 whitespace-nowrap">Used</th>
                                          <th className="text-right font-semibold py-2.5 px-2 whitespace-nowrap">Remaining</th>
                                          <th className="text-left font-semibold py-2.5 px-2 min-w-[7rem]">Usage</th>
                                          <th className="text-left font-semibold py-2.5 pl-2 pr-3 min-w-[8rem]">Notes</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {rows.map((row) => {
                                          const usedPct = row.total > 0 ? clamp01(row.used / row.total) : 0;
                                          const remPct = row.total > 0 ? clamp01(row.remaining / row.total) : 0;

                                          return (
                                            <tr
                                              key={row.key}
                                              className="border-b border-border/40 last:border-0 hover:bg-muted/20"
                                            >
                                              <td className="py-2.5 pl-3 pr-2 align-top">
                                                <p className="font-medium text-foreground">{row.title}</p>
                                                {row.slug ? (
                                                  <p className="text-[11px] text-muted-foreground mt-0.5 font-mono truncate max-w-[14rem]" title={row.slug}>
                                                    {row.slug}
                                                  </p>
                                                ) : null}
                                              </td>
                                              <td className="py-2.5 px-2 text-right tabular-nums text-foreground align-top">
                                                {row.daysAllowed != null ? row.daysAllowed : "—"}
                                              </td>
                                              <td className="py-2.5 px-2 text-right tabular-nums text-muted-foreground align-top">
                                                {row.maxReq != null ? row.maxReq : "—"}
                                              </td>
                                              <td className="py-2.5 px-2 text-right tabular-nums text-muted-foreground align-top">
                                                {row.maxMo != null ? row.maxMo : "—"}
                                              </td>
                                              <td className="py-2.5 px-2 text-right tabular-nums font-medium text-foreground align-top">
                                                {row.total.toFixed(2)}
                                              </td>
                                              <td className="py-2.5 px-2 text-right tabular-nums text-foreground align-top">
                                                {row.used.toFixed(2)}
                                              </td>
                                              <td className="py-2.5 px-2 text-right tabular-nums text-emerald-700 dark:text-emerald-400 align-top">
                                                {row.remaining.toFixed(2)}
                                              </td>
                                              <td className="py-2.5 px-2 align-top">
                                                <div className="h-1.5 w-full max-w-[6rem] rounded-full bg-muted/60 overflow-hidden">
                                                  <div
                                                    className="h-full rounded-full bg-primary/85"
                                                    style={{ width: `${Math.round(usedPct * 100)}%` }}
                                                  />
                                                </div>
                                                <p className="text-[10px] text-muted-foreground mt-1 tabular-nums">
                                                  {Math.round(usedPct * 100)}% used · {Math.round(remPct * 100)}% left
                                                </p>
                                              </td>
                                              <td className="py-2.5 px-2 align-top text-[10px] text-muted-foreground leading-snug max-w-[14rem]">
                                                {row.showCarryNote ? (
                                                  <p>
                                                    <span className="font-medium text-emerald-700 dark:text-emerald-400">Carry</span>{" "}
                                                    base {typeof row.baseAllowed === "number" ? row.baseAllowed : "—"}, prev{" "}
                                                    {row.carriedForward.toFixed(2)}, pool {row.total}
                                                  </p>
                                                ) : null}
                                                {row.showEncashNote ? (
                                                  <p className={row.showCarryNote ? "mt-1" : ""}>
                                                    <span className="font-medium text-amber-800 dark:text-amber-300">Encash</span>{" "}
                                                    prev {row.encashedFromPrev.toFixed(2)}, combined{" "}
                                                    {typeof row.baseAllowed === "number"
                                                      ? (row.baseAllowed + row.encashedFromPrev).toFixed(2)
                                                      : row.encashedFromPrev.toFixed(2)}
                                                  </p>
                                                ) : null}
                                                {!row.showCarryNote && !row.showEncashNote ? "—" : null}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </TabsContent>
                      );
                    })}
                  </Tabs>
          </DetailSection>
          );

          const shiftSection = () => (
          <DetailSection title="Employee shift" icon={Clock}>
                  {workShiftCurrent ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                      <div className="rounded-xl border border-border/50 bg-muted/10 p-4 min-w-0">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                              Current work shift
                            </p>
                            <p className="text-sm font-semibold text-foreground mt-1 break-words">
                              {workShiftCurrent.workShiftName?.trim() ? workShiftCurrent.workShiftName : "—"}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatTimeDisplayAmPm(workShiftCurrent.startTime)} – {formatTimeDisplayAmPm(workShiftCurrent.endTime)}
                              {workShiftCurrent.crossesMidnight ? " (+1)" : ""} · {workShiftCurrent.breakMinutes}m break
                            </p>
                            <p className="text-[11px] text-muted-foreground mt-2">
                              Effective: {workShiftCurrent.effectiveFrom || "—"} → {workShiftCurrent.effectiveTo || "Present"}
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {workShiftCurrent.crossesMidnight ? (
                              <span className="text-[10px] px-2 py-0.5 rounded-full border border-violet-500/25 bg-violet-500/10 text-violet-800 dark:text-violet-300 font-semibold">
                                Night
                              </span>
                            ) : null}
                            <span
                              className={cn(
                                "text-[10px] px-2 py-0.5 rounded-full border font-semibold",
                                workShiftCurrent.active
                                  ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300"
                                  : "border-border/50 bg-muted/40 text-muted-foreground",
                              )}
                            >
                              {workShiftCurrent.active ? "Active" : "Inactive"}
                            </span>
                          </div>
                        </div>
                        {workShiftCurrent.workShiftSlug?.trim() ? (
                          <p className="mt-3 text-[11px] text-muted-foreground font-mono truncate" title={workShiftCurrent.workShiftSlug}>
                            {workShiftCurrent.workShiftSlug}
                          </p>
                        ) : null}
                      </div>

                      <div className="rounded-xl border border-border/50 bg-muted/10 p-4 min-w-0">
                        <p className="text-xs font-semibold text-foreground">Assignment history</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">Previous and current work shift assignments.</p>

                        <div className="mt-3 overflow-x-auto">
                          <table className="w-full text-sm min-w-[18rem]">
                            <thead>
                              <tr className="text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border/50">
                                <th className="text-left font-semibold py-2 pr-3">Shift</th>
                                <th className="text-left font-semibold py-2 px-3">Effective</th>
                                <th className="text-right font-semibold py-2 pl-3">Hours</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(employee.workShiftAssignments ?? [])
                                .slice()
                                .sort((a, b) => (b.effectiveFrom || "").localeCompare(a.effectiveFrom || ""))
                                .map((a) => {
                                  const label = a.workShiftName?.trim() ? a.workShiftName : a.workShiftId;
                                  const range = `${a.effectiveFrom || "—"} → ${a.effectiveTo || "Present"}`;
                                  const hours = `${formatTimeDisplayAmPm(a.startTime)} – ${formatTimeDisplayAmPm(a.endTime)}${a.crossesMidnight ? " (+1)" : ""}`;
                                  const isCurrent = !a.effectiveTo;
                                  return (
                                    <tr key={`${a.workShiftId}-${a.effectiveFrom}-${a.effectiveTo}`} className="border-b border-border/30 last:border-0">
                                      <td className="py-2 pr-3">
                                        <span className="font-medium text-foreground">{label || "—"}</span>
                                        {isCurrent ? (
                                          <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 font-semibold">
                                            Current
                                          </span>
                                        ) : null}
                                      </td>
                                      <td className="py-2 px-3 text-muted-foreground">{range}</td>
                                      <td className="py-2 pl-3 text-right text-muted-foreground tabular-nums">{hours}</td>
                                    </tr>
                                  );
                                })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-border/50 bg-muted/10 p-4">
                      <p className="text-sm text-muted-foreground">No work shift assigned.</p>
                      <p className="text-[11px] text-muted-foreground mt-1">
                        Assign one from{" "}
                        <Link to={`/employees/${employee.id}/edit`} className="text-primary font-medium hover:underline">
                          Edit employee
                        </Link>
                        .
                      </p>
                    </div>
                  )}
          </DetailSection>
          );

          const workPolicySection = () => {
            const pid = (employee.workPolicyId ?? "").trim();
            const pname = (employee.workPolicyName ?? "").trim();
            const title = pname || (pid ? `Policy #${pid}` : "");
            return (
              <DetailSection title="Work policy" icon={FileCheck2}>
                {pid ? (
                  <div className="rounded-xl border border-border/50 bg-muted/10 p-4 space-y-2">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Assigned policy</p>
                      <p className="text-sm font-semibold text-foreground mt-1 break-words">{title || "—"}</p>
                      <p className="text-[11px] text-muted-foreground font-mono mt-1">ID {pid}</p>
                    </div>
                    <p className="text-[11px] text-muted-foreground pt-1 border-t border-border/40">
                      Policies bundle attendance rules for this role.{" "}
                      <Link to="/work-policies" className="text-primary font-medium hover:underline">
                        Work policies
                      </Link>
                      {" · "}
                      <Link to={`/employees/${employee.id}/edit`} className="text-primary font-medium hover:underline">
                        Change in edit
                      </Link>
                    </p>
                  </div>
                ) : (
                  <div className="rounded-xl border border-border/50 bg-muted/10 p-4">
                    <p className="text-sm text-muted-foreground">No work policy assigned.</p>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Assign one from{" "}
                      <Link to={`/employees/${employee.id}/edit`} className="text-primary font-medium hover:underline">
                        Edit employee
                      </Link>
                      {" "}
                      (only active policies with attendance rules are listed).
                    </p>
                  </div>
                )}
              </DetailSection>
            );
          };

          const salaryPolicySection = () => {
            const pid = (employee.salaryPolicyId ?? "").trim();
            const pname = (employee.salaryPolicyName ?? "").trim();
            const title = pname || (pid ? `Policy #${pid}` : "");
            return (
              <DetailSection title="Salary policy" icon={Banknote}>
                {pid ? (
                  <div className="rounded-xl border border-border/50 bg-muted/10 p-4 space-y-2">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Assigned policy</p>
                      <p className="text-sm font-semibold text-foreground mt-1 break-words">{title || "—"}</p>
                      <p className="text-[11px] text-muted-foreground font-mono mt-1">ID {pid}</p>
                    </div>
                    <p className="text-[11px] text-muted-foreground pt-1 border-t border-border/40">
                      Policies bundle salary rules for payroll.{" "}
                      <Link to="/salary-policies" className="text-primary font-medium hover:underline">
                        Salary policies
                      </Link>
                      {" · "}
                      <Link to={`/employees/${employee.id}/edit`} className="text-primary font-medium hover:underline">
                        Change in edit
                      </Link>
                    </p>
                  </div>
                ) : (
                  <div className="rounded-xl border border-border/50 bg-muted/10 p-4">
                    <p className="text-sm text-muted-foreground">No salary policy assigned.</p>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Assign one from{" "}
                      <Link to={`/employees/${employee.id}/edit`} className="text-primary font-medium hover:underline">
                        Edit employee
                      </Link>
                      {" "}
                      (only active policies with salary rules are listed).
                    </p>
                  </div>
                )}
              </DetailSection>
            );
          };

          return isLgUp ? (
            <div className="flex flex-row gap-4 items-start">
              <div className="flex flex-col gap-4 flex-1 min-w-0">
                {basicSection()}
                {salarySection()}
                {leaveSection()}
              </div>
              <div className="flex flex-col gap-4 flex-1 min-w-0">
                {employmentSection()}
                {bankSection()}
                {shiftSection()}
                {workPolicySection()}
                {salaryPolicySection()}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {basicSection()}
              {employmentSection()}
              {salarySection()}
              {bankSection()}
              {leaveSection()}
              {shiftSection()}
              {workPolicySection()}
              {salaryPolicySection()}
            </div>
          );
        })()}

        <ConfirmAlertDialog
          open={confirmDelete}
          onOpenChange={setConfirmDelete}
          title="Delete this employee?"
          description="This will remove the employee from the list. This action cannot be undone."
          mode="confirm"
          variant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={async () => {
            if (!canDelete) {
              toast.error("User does not have the right permissions.");
              setConfirmDelete(false);
              return;
            }
            try {
              const res = await deleteEmployee(employee.id);
              toast.success(res.message);
              setConfirmDelete(false);
              navigate("/employees");
            } catch (e) {
              toast.error(employeeApiErrorMessage(e));
              setConfirmDelete(false);
            }
          }}
        />
      </div>
    </Layout>
  );
}
